<template>
  <div class="results">
    <h2 class="section-title large">Results</h2>

    <p class="description">
      Stay updated with daily Lucky Numbers draws, all week long. See upcoming draws, track results, and never miss a moment of the action.
    </p>
    <!-- Filter Tabs -->
    <div class="filter-tabs">
      <button 
        v-for="tab in tabs" 
        :key="tab.id"
        class="tab-button"
        :class="{ active: activeTab === tab.id }"
        @click="activeTab = tab.id"
      >
        {{ tab.label }}
      </button>
    </div>

    <!-- Filter Content -->
    <div class="filter-content">
      <!-- By Date Filter -->
      <div v-if="activeTab === 'by-date'" class="date-filter">
        <NextDrawsFilter
          v-model="selectedDate"
          @update:modelValue="handleDateFilter"
          :show-date-picker="showDatePicker"
          :allow-past="true"
        />
      </div>

      <!-- By Game Filter -->
      <div v-if="activeTab === 'by-lottery'" class="game-filter">
        <select v-model="selectedGame" class="game-select">
          <option value="">All Games</option>
          <option 
            v-for="game in gamesList" 
            :key="game.slug"
            :value="game.slug"
          >
            {{ game.name }}
          </option>
        </select>
      </div>

      <!-- By Draw ID Filter -->
      <div v-if="activeTab === 'by-drawid'" class="draw-filter">
        <input 
          v-model="drawId" 
          type="text" 
          placeholder="Enter Draw ID"
          class="draw-input"
        />
      </div>
    </div>

    <!-- Results Grid -->
    <div class="results-grid">
      <LotteryResultCard
        v-for="result in paginatedResults"
        :key="result.id"
        :lottery="result"
        @bet="handleBet(result)"
      />
    </div>

    <!-- Load More Button -->
    <div v-if="hasMoreResults" class="load-more">
      <button class="load-more-button" @click="loadMore">
        Load More Results
      </button>
    </div>

    <!-- Results Info -->
    <div class="results-info">
      <p>
        With Lucky Numbers draws every day, seven days per week, keeping your finger on every pulse can be overwhelming. <br />
        We keep you up to date with all the information you need so you do not miss out on the action!<br />
        We detail all the upcoming draws for each day, as well as informing you of the results of every draw.<br />
        Stay on top of the days events as well as all the upcoming draws of the week ahead!
      </p>
    </div>

    <div v-if="resultsStore.loading">Loading...</div>
    <div v-else-if="resultsStore.error">{{ resultsStore.error }}</div>
  </div>
</template>

<script setup lang="ts">
import { ref, computed, watch, onMounted } from 'vue'
import { defineOptions } from 'vue'
import LotteryResultCard from '@/components/LotteryResultCard.vue'
import NextDrawsFilter from '@/components/NextDrawsFilter.vue'
import { useResultsStore } from '@/stores/results'
import { useGameStore } from '@/stores/game'
import { useRouter } from 'vue-router'
// import { useAuthStore } from '@/stores/auth'
defineOptions({
  name: 'ResultsView'
})

const showDatePicker = ref(false);
const gameStore = useGameStore()
const resultsStore = useResultsStore();
// const authStore = useAuthStore()
const router = useRouter()
const tabs = [
  { id: 'by-date', label: 'BY DATE' },
  { id: 'by-lottery', label: 'BY GAME' },
  { id: 'by-drawid', label: 'BY DRAW ID' }
];

const activeTab = ref('by-lottery'); // Default to BY GAME
const selectedDate = ref(new Date().toISOString().split('T')[0]);
const selectedGame = ref('');
const drawId = ref('');

const RESULTS_PER_PAGE = 10;
const currentPage = ref(1);

const paginatedResults = computed(() => {
  const start = (currentPage.value - 1) * RESULTS_PER_PAGE;
  const end = start + RESULTS_PER_PAGE;
  return resultsStore.results.slice(0, end);
});

const hasMoreResults = computed(() => {
  return resultsStore.results.length > paginatedResults.value.length;
});

const loadMore = () => {
  if (hasMoreResults.value) {
    currentPage.value += 1;
  }
};

const handleDateFilter = (date: string) => {
  selectedDate.value = date || new Date().toISOString().split('T')[0];
};

const handleBet = async (game: any) => {
  await gameStore.fetchGameDetails(game.game_slug)
  router.push(`/lottery/${game?.game_slug}`);
}

const gamesList = computed(() => {
  // Use game store to show ALL games, not just games with results
  const allGames = gameStore.getProducts || [];
  if (!Array.isArray(allGames)) return [];
  
  // Sort games alphabetically by name
  return [...allGames].sort((a, b) => {
    const nameA = a.name?.toLowerCase() || '';
    const nameB = b.name?.toLowerCase() || '';
    return nameA.localeCompare(nameB);
  });
});

watch(activeTab, (newTab, oldTab) => {
  if (oldTab === 'by-lottery' && newTab !== 'by-lottery') {
    selectedGame.value = '';
  }
});

watch(
  [activeTab, selectedDate, selectedGame, drawId],
  () => {
    currentPage.value = 1;
    const filters: any = {
      drawDate: '',
      drawId: '',
      drawType: '',
      gameSlug: ''
    };

    if (activeTab.value === 'by-date') {
      filters.drawType = 'by-date';
      filters.drawDate = selectedDate.value || '';
    } else if (activeTab.value === 'by-lottery') {
      filters.drawType = 'by-lottery';
      filters.gameSlug = selectedGame.value || '';
    } else if (activeTab.value === 'by-drawid') {
      filters.drawType = 'draw';
      filters.drawId = drawId.value || '';
    }

    resultsStore.setFilters(filters);
    resultsStore.fetchResults(1);
  }
);

onMounted(async () => {
  // await authStore.authenticate()
  // Fetch all products (games) to populate the dropdown immediately
  if (!gameStore.getProducts || gameStore.getProducts.length === 0) {
    await gameStore.fetchProducts();
  }
  // Set filters for BY GAME mode by default
  resultsStore.setFilters({ 
    drawType: 'by-lottery',
    gameSlug: selectedGame.value || ''
  });
  resultsStore.fetchResults(1);
});
</script>

<style scoped>
.results {
  max-width: 1200px;
  margin: 0 auto;
}

.draw-filter {
  text-align: center;
  border: 1px solid var(--border-color);
  color: var(--text-primary);
}
/* Custom styles specific to ResultsView */

.filter-tabs {
  display: flex;
  gap: 8px;
  margin-bottom: 24px;
  justify-content: center;
  width: 849px;
  height: 74px;
  margin-left: auto;
  margin-right: auto;
  background: transparent !important;
}

.filter-content {
  margin-bottom: 32px;
  display: flex;
  justify-content: center;
}

.date-button,
.game-select,
.draw-input {
  background:  var(--bg-filter-select, rgba(255, 255, 255, 0.1));
  border: 1px solid var(--border-color);
  color: var(--text-primary);
  padding: 12px 24px;
  font-size: 16px;
  min-width: 300px;
  text-align: center;
}

.date-button:focus,
.game-select:focus,
.draw-input:focus, input:focus {
  border-color: var(--bg-filter-select-focus, var(--border-color));
  outline: var(--bg-filter-select-focus, var(--border-color));
}

.date-button {
  display: flex;
  align-items: center;
  justify-content: space-between;
  cursor: pointer;
}

.calendar-icon {
  font-size: 20px;
}

.results-grid {
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 16px;
  margin-bottom: 32px;
}

.load-more {
  display: flex;
  justify-content: center;
  margin-bottom: 48px;
}

.results-info {
  text-align: center;
  color: var(--text-light);
  max-width: 1200px;
  margin: 0 auto;
}

.results-info p {
  margin-bottom: 8px;
}

.see-more-button {
  background: var(--bg-danger);
  border: none;
  color: var(--text-primary);
  font-weight: 500;
  cursor: pointer;
  padding: 8px 16px;
  margin-top: 16px;
}

@media (max-width: 1024px) {
  .results-grid {
    grid-template-columns: 1fr;
  }
}

@media (max-width: 768px) {
  .results {
    padding: 0 4px;
  }

  .results-grid {
    margin-left: 0;
    margin-right: 0;
  }


  .filter-tabs {
    width: 100%;
  }

  .date-filter {
    width: 100%;
  }

  .date-button,
  .game-select,
  .draw-input {
    min-width: 250px;
    font-size: 14px;
  }
}

.game-logo {
  width: 20px;
  height: 20px;
  vertical-align: middle;
  margin-right: 6px;
}

.game-select {
  display: flex;
  align-items: center;
  min-width: 220px;
  padding: 12px 12px;
  font-size: 16px;
  background: var(--bg-primary);
  color: var(--text-primary);
  border-radius: 4px;
  border: 1px solid var(--border-color);
  text-align: center;
}
</style>